import React, { useState, useEffect } from 'react';
import { XMarkIcon, ChevronDownIcon, DocumentDuplicateIcon, SparklesIcon, CheckCircleIcon } from '@heroicons/react/24/solid';
import { fetchBudgets, bulkCreateTransactions, aiAutoReview, Budget, fetchMappingProfiles, createMappingProfile, updateMappingProfile, MappingProfile, fetchImportRules, createImportRule, updateImportRule, ImportRule } from '../api/budgets';
import LoadingSpinner from './LoadingSpinner';
import ConfirmDialog from './ConfirmDialog';

interface CSVImportOverlayProps {
  csvData: string;
  csvHeaders: string[];
  onClose: () => void;
  onConfirmMapping: (mappings: { amount: string; datetime: string; note: string }) => void;
}

interface ImportedTransaction {
  id: string;
  amount: string;
  datetime: string;
  note: string;
  budget: string | null;
  skip: boolean;
  originalRow: string[];
  originalNote: string; // Track original note for change detection
  hasNoteChanged: boolean; // Track if note has been modified
  appliedRuleId?: string; // Track which rule was applied to this transaction
}

export function CSVImportOverlay({
  csvData,
  csvHeaders,
  onClose,
  onConfirmMapping,
}: CSVImportOverlayProps) {
  const [stage, setStage] = useState<'mapping' | 'reconciliation'>('mapping');
  const [mappings, setMappings] = useState({
    amount: '',
    datetime: '',
    note: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [importedTransactions, setImportedTransactions] = useState<ImportedTransaction[]>([]);
  const [budgets, setBudgets] = useState<Budget[]>([]);
  const [visibleTransactions, setVisibleTransactions] = useState(20); // For virtual scrolling
  const [isAiReviewing, setIsAiReviewing] = useState(false);
  const [aiReviewComplete, setAiReviewComplete] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [mappingProfiles, setMappingProfiles] = useState<MappingProfile[]>([]);
  const [selectedProfileId, setSelectedProfileId] = useState<string>('');
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [showNameDialog, setShowNameDialog] = useState(false);
  const [profileName, setProfileName] = useState('');
  const [importRules, setImportRules] = useState<ImportRule[]>([]);
  const [showAddRuleDialog, setShowAddRuleDialog] = useState(false);
  const [selectedTransactionForRule, setSelectedTransactionForRule] = useState<ImportedTransaction | null>(null);
  const [ruleNoteMatchType, setRuleNoteMatchType] = useState<'Equals' | 'Includes'>('Equals');
  const [ruleNoteMatchValue, setRuleNoteMatchValue] = useState('');
  const [ruleAmountMatch, setRuleAmountMatch] = useState('');
  const [ruleUpdatedNote, setRuleUpdatedNote] = useState('');
  const [ruleBudget, setRuleBudget] = useState('');
  const [isSavingRule, setIsSavingRule] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null);

  // Get the first data row for example
  const lines = csvData.split('\n').filter(line => line.trim());
  const firstDataRow = lines.length > 1 ? lines[1].split(',') : [];
  const isAllMapped = mappings.amount && mappings.datetime && mappings.note;

  // Load budgets and import rules when entering reconciliation stage
  useEffect(() => {
    if (stage === 'reconciliation') {
      fetchBudgets().then(setBudgets).catch(console.error);
      fetchImportRules().then(rules => {
        setImportRules(rules);
      }).catch(console.error);
    }
  }, [stage]);

  // Apply rules when they're loaded and transactions exist
  useEffect(() => {
    if (stage === 'reconciliation' && importRules.length > 0 && importedTransactions.length > 0) {
      // Use a small delay to ensure state is updated
      const timer = setTimeout(() => {
        applyImportRulesWithRules(importRules);
      }, 100);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importRules.length, stage]); // Only depend on length, not the array itself

  // Apply import rules helper function
  const applyImportRulesWithRules = (rulesToApply: ImportRule[]) => {
    setImportedTransactions(prev => {
      return prev.map(transaction => {
        // Find matching rule
        for (const rule of rulesToApply) {
          // Check note matching
          let noteMatches = false;
          if (rule.noteMatchType === 'Equals') {
            noteMatches = transaction.originalNote === rule.noteMatchValue;
          } else if (rule.noteMatchType === 'Includes') {
            noteMatches = transaction.originalNote.includes(rule.noteMatchValue);
          }

          // Check amount matching if rule has amount
          let amountMatches = true;
          if (rule.amountMatch !== null && rule.amountMatch !== undefined) {
            const transactionAmount = parseFloat(transaction.amount) || 0;
            amountMatches = Math.abs(transactionAmount - rule.amountMatch) < 0.01; // Allow for floating point precision
          }

          // If both match, apply the rule
          if (noteMatches && amountMatches) {
            // If rule has skip enabled, skip the transaction
            if (rule.skip) {
              return {
                ...transaction,
                skip: true,
                budget: null,
                appliedRuleId: rule._id
              };
            }
            // Otherwise, apply the rule normally
            return {
              ...transaction,
              note: rule.updatedNote,
              budget: rule.budget ? (typeof rule.budget === 'string' ? rule.budget : rule.budget._id) : null,
              hasNoteChanged: rule.updatedNote !== transaction.originalNote,
              appliedRuleId: rule._id
            };
          }
        }
        return transaction;
      });
    });
  };

  // Load mapping profiles when entering mapping stage
  useEffect(() => {
    if (stage === 'mapping') {
      fetchMappingProfiles().then(setMappingProfiles).catch(console.error);
    }
  }, [stage]);

  // Handle profile selection
  useEffect(() => {
    if (selectedProfileId) {
      const profile = mappingProfiles.find(p => p._id === selectedProfileId);
      if (profile) {
        setMappings({
          datetime: profile.datetimeField,
          amount: profile.amountField,
          note: profile.noteField,
        });
      }
    }
  }, [selectedProfileId, mappingProfiles]);

  // Auto-load more transactions when scrolling near bottom
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100; // 100px threshold

    if (isNearBottom && visibleTransactions < importedTransactions.length) {
      handleLoadMore();
    }
  };

  // Parse CSV data and transform to transactions
  const parseCSVTransactions = (): ImportedTransaction[] => {
    const transactions: ImportedTransaction[] = [];
    const headerIndices = {
      amount: csvHeaders.indexOf(mappings.amount),
      datetime: csvHeaders.indexOf(mappings.datetime),
      note: csvHeaders.indexOf(mappings.note),
    };

    // Skip header row, process data rows
    for (let i = 1; i < lines.length; i++) {
      const row = lines[i].split(',');
      if (row.length === 0 || row.every(cell => !cell.trim())) continue;

      // Strip quotes from all fields and clean amount field
      const amountRaw = row[headerIndices.amount]?.replace(/"/g, '').replace(/\s/g, '') || '';
      const datetimeRaw = row[headerIndices.datetime]?.replace(/"/g, '').trim() || '';
      const note = row[headerIndices.note]?.replace(/"/g, '').trim() || '';
      
      // Clean amount field: remove currency symbols, commas, and other non-numeric characters except decimal point and minus sign
      const amount = amountRaw.replace(/[^\d.-]/g, '') || '';
      
      // Debug logging for amount parsing
      if (amountRaw && amountRaw !== amount) {
        console.log('Amount cleaned:', { original: amountRaw, cleaned: amount, parsed: parseFloat(amount) });
      }

      // Transform datetime to ISO format
      let datetime = '';
      try {
        if (datetimeRaw) {
          // Check if the original data contains time information
          const hasTime = /\d{1,2}:\d{1,2}/.test(datetimeRaw) || /AM|PM|am|pm/.test(datetimeRaw);

          // Try to parse various date formats
          const date = new Date(datetimeRaw);
          if (!isNaN(date.getTime())) {
            datetime = date.toISOString();
          } else {
            // If parsing fails, try to construct from common formats
            // Assume YYYY-MM-DD or MM/DD/YYYY format
            const dateParts = datetimeRaw.split(/[-/]/);
            if (dateParts.length === 3) {
              let year, month, day;
              if (datetimeRaw.includes('/')) {
                // MM/DD/YYYY format
                month = parseInt(dateParts[0]) - 1;
                day = parseInt(dateParts[1]);
                year = parseInt(dateParts[2]);
              } else {
                // YYYY-MM-DD format
                year = parseInt(dateParts[0]);
                month = parseInt(dateParts[1]) - 1;
                day = parseInt(dateParts[2]);
              }
              const constructedDate = new Date(year, month, day);
              if (!isNaN(constructedDate.getTime())) {
                datetime = constructedDate.toISOString();
              }
            }
          }

          // If no time is present in original data, keep as date-only (YYYY-MM-DD)
          if (datetime && !hasTime) {
            // Keep the date part only for cleaner display
            datetime = datetime.split('T')[0];
          }
        }
      } catch (error) {
        console.warn('Failed to parse datetime:', datetimeRaw);
        datetime = new Date().toISOString().split('T')[0]; // Default to today's date
      }

      transactions.push({
        id: `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        amount,
        datetime,
        note,
        budget: null,
        skip: false,
        originalRow: row,
        originalNote: note,
        hasNoteChanged: false,
      });
    }

    return transactions;
  };

  const handleMappingChange = (field: 'amount' | 'datetime' | 'note', value: string) => {
    setMappings(prev => ({
      ...prev,
      [field]: value,
    }));
    // Clear selected profile when user manually changes mappings
    if (selectedProfileId) {
      setSelectedProfileId('');
    }
  };

  const handleSaveMappingProfile = async () => {
    if (!isAllMapped) return;

    if (selectedProfileId) {
      // Update existing profile directly
      try {
        setIsSavingProfile(true);

        await updateMappingProfile(selectedProfileId, {
          name: mappingProfiles.find(p => p._id === selectedProfileId)?.name || 'Untitled Profile',
          datetimeField: mappings.datetime,
          amountField: mappings.amount,
          noteField: mappings.note,
        });

        // Refresh profiles list
        const updatedProfiles = await fetchMappingProfiles();
        setMappingProfiles(updatedProfiles);
      } catch (error) {
        console.error('Error updating mapping profile:', error);
        alert(`Failed to update mapping profile: ${error instanceof Error ? error.message : 'Unknown error'}`);
      } finally {
        setIsSavingProfile(false);
      }
    } else {
      // Show dialog to enter name for new profile
      setProfileName('');
      setShowNameDialog(true);
    }
  };

  const handleConfirmSaveProfile = async () => {
    if (!profileName || !profileName.trim()) {
      return; // Don't save if name is empty
    }

    try {
      setIsSavingProfile(true);
      setShowNameDialog(false);

      await createMappingProfile({
        name: profileName.trim(),
        datetimeField: mappings.datetime,
        amountField: mappings.amount,
        noteField: mappings.note,
      });

      // Refresh profiles list
      const updatedProfiles = await fetchMappingProfiles();
      setMappingProfiles(updatedProfiles);

      // Select the newly created profile
      const newProfile = updatedProfiles[0]; // Newest profile is first
      if (newProfile) {
        setSelectedProfileId(newProfile._id);
      }
    } catch (error) {
      console.error('Error saving mapping profile:', error);
      alert(`Failed to save mapping profile: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsSavingProfile(false);
    }
  };


  const handleConfirmMapping = async () => {
    if (!isAllMapped) return;

    try {
      setIsSubmitting(true);

      // Parse and transform CSV transactions
      const transactions = parseCSVTransactions();
      setImportedTransactions(transactions);

      // Move to reconciliation stage
      setStage('reconciliation');

      // Rules will be applied when they're loaded in the reconciliation stage useEffect
      onConfirmMapping(mappings);
    } catch (error) {
      console.error('Error processing CSV:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTransactionUpdate = (id: string, field: keyof ImportedTransaction, value: string | null) => {
    setImportedTransactions(prev =>
      prev.map(transaction => {
        if (transaction.id === id) {
          const updated = { ...transaction, [field]: value };

          // Track if note has changed
          if (field === 'note') {
            updated.hasNoteChanged = value !== transaction.originalNote;
          }

          return updated;
        }
        return transaction;
      })
    );
  };

  const handleBulkUpdateNotes = (transactionId: string, newNote: string) => {
    setImportedTransactions(prev => {
      const transaction = prev.find(t => t.id === transactionId);
      if (!transaction) return prev;

      const originalNote = transaction.originalNote;

      return prev.map(t => {
        if (t.originalNote === originalNote && t.id !== transactionId) {
          return {
            ...t,
            note: newNote,
            hasNoteChanged: newNote !== t.originalNote
          };
        }
        return t;
      });
    });
  };

  const handleBulkUpdateBudgets = (transactionId: string, newBudget: string) => {
    setImportedTransactions(prev => {
      const transaction = prev.find(t => t.id === transactionId);
      if (!transaction || !newBudget) return prev;

      const originalNote = transaction.originalNote;

      return prev.map(t => {
        if (t.originalNote === originalNote && t.id !== transactionId) {
          if (newBudget === '__skip__') {
            // Copy skip state
            return {
              ...t,
              budget: null,
              skip: true
            };
          } else {
            // Copy budget, clear skip if it was set
            return {
              ...t,
              budget: newBudget,
              skip: false
            };
          }
        }
        return t;
      });
    });
  };

  const handleLoadMore = () => {
    setVisibleTransactions(prev => Math.min(prev + 20, importedTransactions.length));
  };

  const handleAiAutoReview = async () => {
    if (importedTransactions.length === 0) return;

    try {
      setIsAiReviewing(true);

      // Send transactions to AI for review
      const result = await aiAutoReview(importedTransactions.map(t => {
        let datetime = t.datetime;

        // Convert date-only format to full ISO format if needed for AI processing
        if (datetime && !datetime.includes('T')) {
          datetime = `${datetime}T00:00:00.000Z`;
        }

        return {
          id: t.id,
          amount: t.amount,
          datetime,
          note: t.note,
          budget: t.budget
        };
      }));

      console.log('AI review completed:', result);

      // Update transactions based on AI response
      setImportedTransactions(prev => {
        const updatedTransactions = prev.map(localTransaction => {
          const aiTransaction = result.transactions.find(ai => ai.id === localTransaction.id);
          if (aiTransaction) {
            let datetime = aiTransaction.datetime;

            // If the original transaction had date-only format, keep it that way for display
            if (localTransaction.datetime && !localTransaction.datetime.includes('T') && datetime.includes('T')) {
              datetime = datetime.split('T')[0];
            }

            return {
              ...localTransaction,
              note: aiTransaction.note,
              budget: aiTransaction.budget,
              datetime,
              hasNoteChanged: aiTransaction.note !== localTransaction.originalNote
            };
          }
          return localTransaction;
        });

        return updatedTransactions;
      });

      setAiReviewComplete(true);

    } catch (error) {
      console.error('AI auto-review failed:', error);
      alert(`AI review failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsAiReviewing(false);
    }
  };

  const displayedTransactions = importedTransactions.slice(0, visibleTransactions);

  // Check if all non-skipped transactions have budgets assigned
  const allNonSkippedHaveBudgets = importedTransactions
    .filter(t => !t.skip)
    .every(t => t.budget && t.budget.trim() !== '');

  const handleImportTransactions = async () => {
    if (!allNonSkippedHaveBudgets) return;

    try {
      setIsSubmitting(true);

      // Get all non-skipped transactions with budgets
      const transactionsToImport = importedTransactions
        .filter(t => !t.skip && t.budget)
        .map(t => {
          let datetime = t.datetime;

          // Convert date-only format to full ISO format if needed
          if (datetime && !datetime.includes('T')) {
            datetime = `${datetime}T00:00:00.000Z`;
          }

          const parsedAmount = parseFloat(t.amount) || 0;
          
          // Debug logging for final amount parsing
          if (t.amount && parsedAmount === 0) {
            console.log('Amount parsing issue:', { 
              original: t.amount, 
              parsed: parsedAmount,
              budget: t.budget,
              note: t.note 
            });
          }
          
          return {
            budget: t.budget!,
            amount: parsedAmount,
            datetime,
            note: t.note
          };
        });

      const result = await bulkCreateTransactions(transactionsToImport);

      // Show success message and close overlay
      setSuccessMessage(`Successfully imported ${result.count} transactions!`);
      setShowSuccessDialog(true);

    } catch (error) {
      console.error('Import failed:', error);
      alert(`Import failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderMappingStage = () => (
    <>
      {/* Header */}
      <div className="flex items-center justify-between py-4 px-8 border-b border-gray-200">
        <span className="text-lg font-semibold text-gray-900 pl-2">Import Transactions</span>
        <button
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors ml-4"
          onClick={onClose}
          aria-label="Close"
        >
          <XMarkIcon className="w-6 h-6 text-gray-600" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-8">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">Map Import Columns</h3>
        <p className="text-sm text-gray-500 mb-8">
          Please map the columns from the imported file to the corresponding transactionfields in Budgie.
        </p>

        {/* Mapping Profile Dropdown */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Mapping Profile (Optional)
          </label>
          <div className="relative">
            <select
              value={selectedProfileId}
              onChange={(e) => setSelectedProfileId(e.target.value)}
              className="w-full appearance-none bg-gray-200 rounded-full px-6 py-3 text-base font-semibold text-gray-800 focus:outline-none select-none cursor-pointer"
              style={{
                backgroundImage: 'none',
                paddingRight: '2.5rem',
                WebkitAppearance: 'none',
                MozAppearance: 'none',
                touchAction: 'manipulation'
              }}
            >
              <option value="">Select a saved profile...</option>
              {mappingProfiles.map((profile) => (
                <option key={profile._id} value={profile._id} className="text-left py-2 px-4">
                  {profile.name}
                </option>
              ))}
            </select>
            <ChevronDownIcon className="absolute right-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-600 pointer-events-none" />
          </div>
        </div>

        {/* Example Row */}
        <div className="bg-gray-50 rounded-lg p-4 mb-8">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Example Data (from imported file):</h4>
          <div className="grid grid-cols-4 gap-2">
            {csvHeaders.map((header, index) => (
              <div key={header} className="bg-white rounded border p-3">
                <div className="text-xs text-gray-500 mb-1">{header}</div>
                <div className="text-sm font-medium text-gray-900">
                  {firstDataRow[index]?.trim() || 'N/A'}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Mapping Fields */}
        <div className="space-y-6">

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date/Time Field
            </label>
            <div className="relative">
              <select
                value={mappings.datetime}
                onChange={(e) => handleMappingChange('datetime', e.target.value)}
                className="w-full appearance-none bg-gray-200 rounded-full px-6 py-3 text-base font-semibold text-gray-800 focus:outline-none select-none cursor-pointer"
                style={{
                  backgroundImage: 'none',
                  paddingRight: '2.5rem',
                  WebkitAppearance: 'none',
                  MozAppearance: 'none',
                  touchAction: 'manipulation'
                }}
              >
                <option value="">Select column...</option>
                {csvHeaders.map((header) => (
                  <option key={header} value={header} className="text-left py-2 px-4">
                    {header}
                  </option>
                ))}
              </select>
              <ChevronDownIcon className="absolute right-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-600 pointer-events-none" />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Amount Field
            </label>
            <div className="relative">
              <select
                value={mappings.amount}
                onChange={(e) => handleMappingChange('amount', e.target.value)}
                className="w-full appearance-none bg-gray-200 rounded-full px-6 py-3 text-base font-semibold text-gray-800 focus:outline-none select-none cursor-pointer"
                style={{
                  backgroundImage: 'none',
                  paddingRight: '2.5rem',
                  WebkitAppearance: 'none',
                  MozAppearance: 'none',
                  touchAction: 'manipulation'
                }}
              >
                <option value="">Select column...</option>
                {csvHeaders.map((header) => (
                  <option key={header} value={header} className="text-left py-2 px-4">
                    {header}
                  </option>
                ))}
              </select>
              <ChevronDownIcon className="absolute right-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-600 pointer-events-none" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Note Field
            </label>
            <div className="relative">
              <select
                value={mappings.note}
                onChange={(e) => handleMappingChange('note', e.target.value)}
                className="w-full appearance-none bg-gray-200 rounded-full px-6 py-3 text-base font-semibold text-gray-800 focus:outline-none select-none cursor-pointer"
                style={{
                  backgroundImage: 'none',
                  paddingRight: '2.5rem',
                  WebkitAppearance: 'none',
                  MozAppearance: 'none',
                  touchAction: 'manipulation'
                }}
              >
                <option value="">Select column...</option>
                {csvHeaders.map((header) => (
                  <option key={header} value={header} className="text-left py-2 px-4">
                    {header}
                  </option>
                ))}
              </select>
              <ChevronDownIcon className="absolute right-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-600 pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Save Mapping Profile Button */}
        <div className="mt-6">
          <button
            className={`w-full py-3 rounded-full text-white text-base font-semibold flex items-center justify-center active:scale-95 transition ${
              isAllMapped && !isSavingProfile
                ? 'bg-emerald-800 hover:bg-emerald-700'
                : 'bg-gray-400 cursor-not-allowed'
            }`}
            onClick={handleSaveMappingProfile}
            disabled={!isAllMapped || isSavingProfile}
          >
            {isSavingProfile ? 'Saving...' : selectedProfileId ? 'Update Mapping Profile' : 'Save Mapping Profile'}
          </button>
        </div>
      </div>

      {/* Confirm Button */}
      <div className="px-8 pb-8">
        <button
          className={`w-full py-4 rounded-full text-white text-xl font-semibold flex items-center justify-center active:scale-95 transition ${
            isSubmitting || !isAllMapped
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-[#22C55E] hover:bg-[#16A34A]'
          }`}
          onClick={handleConfirmMapping}
          disabled={isSubmitting || !isAllMapped}
        >
          {isSubmitting ? 'Processing...' : 'Next'}
        </button>
      </div>
    </>
  );

  const renderReconciliationStage = () => (
    <>
      {/* Header */}
      <div className="flex items-center justify-between py-4 px-8 border-b border-gray-200 mb-4">
        <span className="text-lg font-semibold text-gray-900">
          Review Transactions ({importedTransactions.filter(t => !t.skip).length} to import)
        </span>
        <button
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors ml-4"
          onClick={onClose}
          aria-label="Close"
        >
          <XMarkIcon className="w-6 h-6 text-gray-600" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-8" onScroll={handleScroll}>
        <div className="max-w-4xl mx-auto">
          <div className="mb-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Review & Assign Budgets</h3>
            <p className="text-gray-600 text-sm">
              Review the imported transactions below. You can edit the values and assign each transaction to a budget.
            </p>

            {/* AI Auto Review Banner */}
            <div className="mt-4 p-4 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm text-purple-800 font-medium">
                    ✨ Let AI help organize your transactions
                  </p>
                  <p className="text-xs text-purple-600 mt-1">
                    AI can improve transaction notes and suggest appropriate budgets (may take a couple of minutes)
                  </p>
                </div>
                {aiReviewComplete ? (
                  <div className="flex items-center gap-2 px-4 py-2 bg-green-100 text-green-800 text-sm font-medium rounded-lg border border-green-200">
                    <CheckCircleIcon className="w-4 h-4" />
                    Auto-Review Complete
                  </div>
                ) : (
                  <button
                    onClick={handleAiAutoReview}
                    disabled={isAiReviewing || importedTransactions.length === 0}
                    className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white text-sm font-medium rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isAiReviewing ? (
                      <LoadingSpinner size="sm" />
                    ) : (
                      <SparklesIcon className="w-4 h-4" />
                    )}
                    {isAiReviewing ? 'AI Reviewing...' : 'Auto Review'}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Transaction List */}
          <div className="space-y-3">
            {displayedTransactions.map((transaction, index) => (
              <div 
                key={transaction.id} 
                className={`rounded-lg border p-4 shadow-sm relative group ${transaction.skip ? 'bg-red-50 border-red-200' : 'bg-white'}`}
              >
                {/* Add/Edit Rule Button - appears on hover */}
                <button
                  onClick={() => {
                    setSelectedTransactionForRule(transaction);
                    if (transaction.appliedRuleId) {
                      // Edit existing rule
                      const rule = importRules.find(r => r._id === transaction.appliedRuleId);
                      if (rule) {
                        setEditingRuleId(rule._id);
                        setRuleNoteMatchType(rule.noteMatchType);
                        setRuleNoteMatchValue(rule.noteMatchValue);
                        setRuleAmountMatch(rule.amountMatch !== null && rule.amountMatch !== undefined ? rule.amountMatch.toString() : '');
                        setRuleUpdatedNote(rule.updatedNote);
                        setRuleBudget(rule.skip ? '__skip__' : (rule.budget ? (typeof rule.budget === 'string' ? rule.budget : rule.budget._id) : ''));
                      }
                    } else {
                      // Create new rule
                      setEditingRuleId(null);
                      setRuleNoteMatchType('Equals');
                      setRuleNoteMatchValue(transaction.originalNote);
                      setRuleAmountMatch('');
                      setRuleUpdatedNote(transaction.note);
                      setRuleBudget(transaction.budget || '');
                    }
                    setShowAddRuleDialog(true);
                  }}
                  className="absolute -top-3 right-2 opacity-0 group-hover:opacity-100 transition-opacity px-3 py-1.5 bg-emerald-800 hover:bg-emerald-700 text-white text-xs font-medium rounded-lg shadow-sm z-10"
                  title={transaction.appliedRuleId ? "Edit Rule" : "Add Rule"}
                >
                  {transaction.appliedRuleId ? 'Edit Rule' : 'Add Rule'}
                </button>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">

                  {/* DateTime */}
                  <div>
                    <div className="flex items-center h-4 mb-1">
                      <label className="text-xs font-medium text-gray-500 flex-1">Date & Time</label>
                    </div>
                    <input
                      type="text"
                      value={transaction.datetime}
                      onChange={(e) => handleTransactionUpdate(transaction.id, 'datetime', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                  
                  {/* Amount */}
                  <div>
                    <div className="flex items-center h-4 mb-1">
                      <label className="text-xs font-medium text-gray-500 flex-1">Amount</label>
                    </div>
                    <input
                      type="text"
                      value={transaction.amount}
                      onChange={(e) => handleTransactionUpdate(transaction.id, 'amount', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>

                  {/* Note */}
                  <div>
                    <div className="flex items-center h-4 mb-1">
                      <label className="text-xs font-medium text-gray-500 flex-1">Note</label>
                      {transaction.hasNoteChanged && (
                        <button
                          onClick={() => handleBulkUpdateNotes(transaction.id, transaction.note)}
                          className="text-xs text-emerald-600 hover:text-emerald-800 p-1 rounded ml-1"
                          title="Copy note to other matching transactions?"
                        >
                          <DocumentDuplicateIcon className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                    <input
                      type="text"
                      value={transaction.note}
                      onChange={(e) => handleTransactionUpdate(transaction.id, 'note', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                    {transaction.hasNoteChanged && transaction.originalNote && (
                      <div className="text-xs text-gray-400 mt-1 truncate" title={`Original: ${transaction.originalNote}`}>
                        Was: {transaction.originalNote}
                      </div>
                    )}
                  </div>

                  {/* Budget */}
                  <div>
                    <div className="flex items-center h-4 mb-1">
                      <label className="text-xs font-medium text-gray-500 flex-1">Budget</label>
                      {transaction.originalNote && transaction.budget && (transaction.budget.trim() !== '' || transaction.budget === '__skip__') && (
                        <button
                          onClick={() => handleBulkUpdateBudgets(transaction.id, transaction.budget!)}
                          className="text-xs text-emerald-600 hover:text-emerald-800 p-1 rounded ml-1"
                          title="Copy budget to other matching transactions?"
                        >
                          <DocumentDuplicateIcon className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                    <select
                      value={transaction.skip ? '__skip__' : (transaction.budget || '')}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '__skip__') {
                          // Skip transaction - update both budget and skip fields
                          handleTransactionUpdate(transaction.id, 'budget', null);
                          setImportedTransactions(prev =>
                            prev.map(t =>
                              t.id === transaction.id ? { ...t, skip: true } : t
                            )
                          );
                        } else {
                          // Normal budget selection - clear skip flag
                          handleTransactionUpdate(transaction.id, 'budget', value);
                          setImportedTransactions(prev =>
                            prev.map(t =>
                              t.id === transaction.id ? { ...t, skip: false } : t
                            )
                          );
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      <option value="">Select budget...</option>
                      <option value="__skip__" className="text-red-600">Skip Transaction</option>
                      {budgets.map((budget) => (
                        <option key={budget._id} value={budget._id}>
                          {budget.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Row Number and Status */}
                <div className="flex justify-between items-center">
                  {/* <div className="text-xs text-gray-400">
                    Row {index + 2} from import file
                  </div> */}
                  {transaction.skip && (
                    <div className="mt-2 text-xs text-red-600 font-medium">
                      🚫 Skipped
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Load More Button */}
          {visibleTransactions < importedTransactions.length && (
            <div className="mt-6 text-center">
              <button
                onClick={handleLoadMore}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
              >
                Load More Transactions ({importedTransactions.length - visibleTransactions} remaining)
              </button>
            </div>
          )}

          {/* Summary */}
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <div className="text-sm text-gray-600 space-y-1">
              <div>Total transactions: {importedTransactions.length}</div>
              <div>Transactions with budgets assigned: {
                importedTransactions.filter(t => t.budget && !t.skip).length
              }</div>
              <div>Transactions without budgets: {
                importedTransactions.filter(t => !t.budget && !t.skip).length
              }</div>
              <div className="text-red-600">Transactions to skip: {
                importedTransactions.filter(t => t.skip).length
              }</div>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="px-8 pb-8 mt-4">
        <div className="flex gap-3">
          <button
            className="flex-1 py-4 rounded-full bg-gray-200 text-gray-700 text-xl font-semibold hover:bg-gray-300 transition"
            onClick={() => setStage('mapping')}
          >
            Back to Mapping
          </button>
          <button
            className={`flex-1 py-4 rounded-full text-xl font-semibold transition ${
              allNonSkippedHaveBudgets && !isSubmitting
                ? 'bg-[#22C55E] text-white hover:bg-[#16A34A]'
                : 'bg-gray-400 text-gray-700 cursor-not-allowed'
            }`}
            onClick={handleImportTransactions}
            disabled={!allNonSkippedHaveBudgets || isSubmitting}
          >
            {isSubmitting
              ? 'Importing...'
              : allNonSkippedHaveBudgets
                ? `Import ${importedTransactions.filter(t => !t.skip).length} Transactions`
                : 'Assign budgets to all transactions'
            }
          </button>
        </div>
      </div>
    </>
  );

  return (
    <>
      <div className="fixed inset-0 z-50 bg-white flex flex-col w-full max-w-4xl mx-auto">
        {stage === 'mapping' ? renderMappingStage() : renderReconciliationStage()}
      </div>

      {/* Success Dialog */}
      <ConfirmDialog
        open={showSuccessDialog}
        title="Import Complete"
        message={successMessage}
        confirmText="OK"
        onConfirm={() => {
          setShowSuccessDialog(false);
          onClose();
        }}
        onCancel={() => {
          setShowSuccessDialog(false);
          onClose();
        }}
        type="success"
      />

      {/* Add Rule Dialog */}
      {showAddRuleDialog && selectedTransactionForRule && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full mx-4 overflow-hidden max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
              <h3 className="text-lg font-semibold text-gray-900">{editingRuleId ? 'Edit Import Rule' : 'Create Import Rule'}</h3>
              <button
                onClick={() => {
                  setShowAddRuleDialog(false);
                  setSelectedTransactionForRule(null);
                  setEditingRuleId(null);
                }}
                className="p-1 rounded-lg hover:bg-gray-100 transition-colors"
                aria-label="Close"
              >
                <XMarkIcon className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 min-h-0 overflow-y-auto p-6">
              <p className="text-sm text-gray-600 mb-6">
                {editingRuleId ? 'Edit the rule to automatically map matching transactions for future imports.' : 'Create a rule to automatically map this transaction for future imports.'}
              </p>

              {/* Transaction Display */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6 space-y-2">
                <div>
                  <span className="text-xs font-medium text-gray-500">Date & Time:</span>
                  <div className="text-sm text-gray-900 mt-1">{selectedTransactionForRule.datetime}</div>
                </div>
                <div>
                  <span className="text-xs font-medium text-gray-500">Amount:</span>
                  <div className="text-sm text-gray-900 mt-1">${selectedTransactionForRule.amount}</div>
                </div>
                <div>
                  <span className="text-xs font-medium text-gray-500">Original Note:</span>
                  <div className="text-sm text-gray-900 mt-1">{selectedTransactionForRule.originalNote}</div>
                </div>
              </div>

              {/* Rule Matching Section */}
              <div className="mb-6">
                <h4 className="text-base font-semibold text-gray-900 mb-4">Rule Matching</h4>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Note
                    </label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <select
                          value={ruleNoteMatchType}
                          onChange={(e) => setRuleNoteMatchType(e.target.value as 'Equals' | 'Includes')}
                          className="w-full appearance-none bg-gray-200 rounded-lg px-4 py-2.5 text-sm font-semibold text-gray-800 focus:outline-none border-0"
                          style={{
                            backgroundImage: 'none',
                            paddingRight: '2.5rem',
                            WebkitAppearance: 'none',
                            MozAppearance: 'none',
                          }}
                        >
                          <option value="Equals">Equals</option>
                          <option value="Includes">Includes</option>
                        </select>
                        <ChevronDownIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-600 pointer-events-none" />
                      </div>
                      <input
                        type="text"
                        value={ruleNoteMatchValue}
                        onChange={(e) => setRuleNoteMatchValue(e.target.value)}
                        className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Note value..."
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Amount (Optional)
                    </label>
                    <input
                      type="text"
                      value={ruleAmountMatch}
                      onChange={(e) => setRuleAmountMatch(e.target.value)}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Leave blank to match any amount"
                    />
                  </div>
                </div>
              </div>

              {/* Rule Result Section */}
              <div>
                <h4 className="text-base font-semibold text-gray-900 mb-4">Rule Result</h4>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Updated Note
                    </label>
                    <input
                      type="text"
                      value={ruleUpdatedNote}
                      onChange={(e) => setRuleUpdatedNote(e.target.value)}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Updated note..."
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Budget
                    </label>
                    <div className="relative">
                      <select
                        value={ruleBudget === '__skip__' ? '__skip__' : ruleBudget}
                        onChange={(e) => setRuleBudget(e.target.value)}
                        className="w-full appearance-none bg-gray-200 rounded-lg px-4 py-2.5 text-sm font-semibold text-gray-800 focus:outline-none border-0"
                        style={{
                          backgroundImage: 'none',
                          paddingRight: '2.5rem',
                          WebkitAppearance: 'none',
                          MozAppearance: 'none',
                        }}
                        required
                      >
                        <option value="">Select budget...</option>
                        <option value="__skip__" className="text-red-600">Skip Transaction</option>
                        {budgets.map((budget) => (
                          <option key={budget._id} value={budget._id}>
                            {budget.name}
                          </option>
                        ))}
                      </select>
                      <ChevronDownIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-600 pointer-events-none" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 p-6 border-t border-gray-200 flex-shrink-0">
              <button
                onClick={() => {
                  setShowAddRuleDialog(false);
                  setSelectedTransactionForRule(null);
                  setEditingRuleId(null);
                }}
                className="flex-1 py-3 px-4 rounded-xl border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!ruleNoteMatchValue.trim() || !ruleUpdatedNote.trim() || !ruleBudget) {
                    return;
                  }

                  try {
                    setIsSavingRule(true);

                    const isSkip = ruleBudget === '__skip__';

                    if (editingRuleId) {
                      // Update existing rule
                      await updateImportRule(editingRuleId, {
                        noteMatchType: ruleNoteMatchType,
                        noteMatchValue: ruleNoteMatchValue.trim(),
                        amountMatch: ruleAmountMatch ? parseFloat(ruleAmountMatch) : null,
                        updatedNote: ruleUpdatedNote.trim(),
                        budget: isSkip ? undefined : ruleBudget,
                        skip: isSkip,
                      });
                    } else {
                      // Create new rule
                      await createImportRule({
                        noteMatchType: ruleNoteMatchType,
                        noteMatchValue: ruleNoteMatchValue.trim(),
                        amountMatch: ruleAmountMatch ? parseFloat(ruleAmountMatch) : null,
                        updatedNote: ruleUpdatedNote.trim(),
                        budget: isSkip ? undefined : ruleBudget,
                        skip: isSkip,
                      });
                    }

                    // Refresh rules list
                    const updatedRules = await fetchImportRules();
                    setImportRules(updatedRules);

                    // Re-apply all rules to current transactions
                    applyImportRulesWithRules(updatedRules);

                    setShowAddRuleDialog(false);
                    setSelectedTransactionForRule(null);
                    setEditingRuleId(null);
                  } catch (error) {
                    console.error('Error saving import rule:', error);
                    alert(`Failed to ${editingRuleId ? 'update' : 'create'} import rule: ${error instanceof Error ? error.message : 'Unknown error'}`);
                  } finally {
                    setIsSavingRule(false);
                  }
                }}
                disabled={!ruleNoteMatchValue.trim() || !ruleUpdatedNote.trim() || (!ruleBudget && ruleBudget !== '__skip__') || isSavingRule}
                className={`flex-1 py-3 px-4 rounded-xl font-medium transition-colors ${
                  ruleNoteMatchValue.trim() && ruleUpdatedNote.trim() && (ruleBudget || ruleBudget === '__skip__') && !isSavingRule
                    ? 'bg-[#22C55E] hover:bg-[#16A34A] text-white'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {isSavingRule ? 'Saving...' : editingRuleId ? 'Update Rule' : 'Save Rule'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Profile Name Input Dialog */}
      {showNameDialog && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full mx-4 overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Save Mapping Profile</h3>
              <button
                onClick={() => setShowNameDialog(false)}
                className="p-1 rounded-lg hover:bg-gray-100 transition-colors"
                aria-label="Close"
              >
                <XMarkIcon className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Profile Name
              </label>
              <input
                type="text"
                value={profileName}
                onChange={(e) => setProfileName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && profileName.trim()) {
                    handleConfirmSaveProfile();
                  }
                }}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
                placeholder="Enter profile name..."
                autoFocus
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3 p-6 pt-0">
              <button
                onClick={() => setShowNameDialog(false)}
                className="flex-1 py-3 px-4 rounded-xl border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmSaveProfile}
                disabled={!profileName || !profileName.trim()}
                className={`flex-1 py-3 px-4 rounded-xl font-medium transition-colors ${
                  profileName && profileName.trim()
                    ? 'bg-[#22C55E] hover:bg-[#16A34A] text-white'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
